import { useState } from "react";
import { motion, useScroll, useTransform, useMotionValueEvent, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV_LINKS, scrollToId } from "@/data";

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { scrollY } = useScroll();
  useMotionValueEvent(scrollY, "change", (v) => setScrolled(v > 60));
  const bgOpacity = useTransform(scrollY, [0, 160], [0, 0.88]);
  const bg = useTransform(bgOpacity, (v) => `rgba(5, 8, 17, ${v})`);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <>
      <motion.header
        data-testid="main-nav"
        style={{ backgroundColor: bg }}
        className={`fixed top-0 left-0 right-0 z-50 backdrop-blur-md transition-[border-color] duration-500 ${
          scrolled ? "border-b border-white/[0.07]" : "border-b border-transparent"
        }`}
      >
        <div className="mx-auto max-w-[1440px] px-6 lg:px-12 h-[72px] flex items-center justify-between">
          <button
            data-testid="nav-logo"
            onClick={() => window.__lenis?.scrollTo(0, { duration: 1.6 })}
            className="flex items-center"
            aria-label="AuraXR home"
          >
            <img src="/images/logo-nav.png" alt="AuraXR" className="h-9 w-auto" />
          </button>
          <nav className="hidden lg:flex items-center gap-8">
            {NAV_LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-link-${l.id}`}
                onClick={() => go(l.id)}
                className="text-[13px] text-slate-300 hover:text-[#FFFDF9] transition-colors duration-300 tracking-wide"
              >
                {l.label}
              </button>
            ))}
            <button
              data-testid="nav-cta-explore"
              onClick={() => go("devices")}
              className="ml-2 rounded-full bg-[#38BDF8] px-5 py-2.5 text-[13px] font-semibold text-[#050811] hover:bg-[#7dd3fc] transition-colors duration-300"
            >
              Explore AuraXR
            </button>
          </nav>
          <button
            data-testid="nav-mobile-menu-button"
            className="lg:hidden text-slate-200"
            onClick={() => setOpen(!open)}
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </motion.header>
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-40 bg-[#050811]/97 backdrop-blur-xl flex flex-col items-center justify-center gap-7 lg:hidden"
          >
            {NAV_LINKS.map((l, i) => (
              <motion.button
                key={l.id}
                data-testid={`nav-mobile-link-${l.id}`}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.06 * i, duration: 0.5 }}
                onClick={() => go(l.id)}
                className="font-display text-2xl text-slate-200 hover:text-[#38BDF8] transition-colors"
              >
                {l.label}
              </motion.button>
            ))}
            <motion.button
              data-testid="nav-mobile-cta-explore"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.5 }}
              onClick={() => go("devices")}
              className="mt-4 rounded-full bg-[#38BDF8] px-8 py-3 text-sm font-semibold text-[#050811]"
            >
              Explore AuraXR
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
